import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
} from "@angular/core";
import { Router } from "@angular/router";

import {
  armarLinkWhatsApp,
  esUrlRenderizable,
  FuentesFoto,
  fuentesFoto,
  ImagenPublica,
  preciosDeCard,
  PropiedadPublica,
  TIPO_PROPIEDAD_SINGULAR,
} from "../../core/models/propiedad.model";
import { FotoPropiedad } from "../foto-propiedad/foto-propiedad";
import { Icono } from "../icono/icono";
import { indiceConWrap, SwipeHorizontal } from "../swipe-horizontal";

/**
 * Tarjeta pública de propiedad — el `list card` del Figma (`244:1572`).
 *
 * La misma tarjeta alimenta la grilla del buscador y el popup del pin del
 * mapa (FP-20). La variante `popup` (Figma `245:1244`) no cambia el contenido
 * ni la estructura: le pone el panel que en la grilla no hace falta —ancho
 * fijo, fondo, radio y sombra—, porque ahí la tarjeta flota sobre el mapa en
 * vez de apoyarse sobre la página. Portada de `PropertyCardComponent` del
 * backoffice, que ya tenía la
 * estructura del Figma (pestañas de logo y precio con el doblez, flechas de
 * carrusel, divider).
 *
 * Se dejó afuera lo que es del backoffice y no aplica acá: el checkbox de
 * selección múltiple, el velo rosa de seleccionado y la insignia `Privada`
 * (decisión D4: las privadas no se listan en el portal).
 *
 * ## Carrusel
 *
 * El componente navega entre las fotos que reciba en `fotos`; con una sola
 * las flechas no se muestran. Se pasa de foto con las flechas o, en una
 * pantalla táctil, arrastrando el dedo sobre ella (RP-11): las flechas sólo
 * aparecen al pasar el mouse por la tarjeta, así que en touch no había forma
 * de ver el resto de la galería. El gesto lo detecta `SwipeHorizontal`, la
 * misma directiva que el carrusel del backoffice. Quién le pasa qué:
 *
 * - **Grilla del buscador:** `propiedad.galeria` — `GET /publico/propiedades`
 *   la devuelve desde BP-03.
 * - **Popup del pin:** la galería del detalle, que el mapa pide al abrir el
 *   popup (FP-36). El contrato del mapa no lleva galería —ver el JSDoc de
 *   `PropiedadListado`—, así que hasta que llegue la respuesta entra `null` y
 *   la tarjeta muestra la portada sola.
 */
@Component({
  selector: "app-propiedad-card",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FotoPropiedad, Icono, SwipeHorizontal],
  templateUrl: "./propiedad-card.html",
  styleUrl: "./propiedad-card.scss",
})
export class PropiedadCard {
  private readonly router = inject(Router);

  readonly propiedad = input.required<PropiedadPublica>();

  /**
   * Fotos a mostrar. Sin esto (o con un array vacío) cae a la portada sola.
   *
   * Tiene que ser una referencia estable: si el llamador arma el array en la
   * plantilla, `indice` se resetea en cada ciclo de detección y el carrusel
   * vuelve solo a la primera foto.
   */
  readonly fotos = input<ImagenPublica[] | null>(null);

  /**
   * Variante del popup del pin (FP-20, Figma `245:1244`): la misma tarjeta
   * sobre su propio panel blanco, para que se lea flotando sobre el mapa.
   */
  readonly popup = input(false);

  /**
   * URL a la que vuelve la ficha (DP07). Por defecto, la actual: tanto la
   * grilla como el mapa quieren volver justo a donde estaban, y el buscador
   * ya persiste todo su estado en los query params.
   */
  readonly volverA = input<string | null>(null);

  // ── Fotos ──────────────────────────────────────────────────────────────────

  /** Galería efectiva: lo que venga por `fotos`, o la portada sola. */
  private readonly galeria = computed<ImagenPublica[]>(() => {
    const explicitas = this.fotos();
    if (explicitas?.length) return explicitas;

    const p = this.propiedad();
    return p.imagenPortada
      ? [{ url: p.imagenPortada, variantes: p.imagenPortadaVariantes }]
      : [];
  });

  /**
   * Índice de la foto visible. `linkedSignal` para que vuelva a cero si la
   * tarjeta se reutiliza con otra propiedad (pasa en el popup del pin, que es
   * el mismo componente para todos los pines).
   */
  private readonly indice = linkedSignal<ImagenPublica[], number>({
    source: this.galeria,
    computation: () => 0,
  });

  readonly tieneVarias = computed(() => this.galeria().length > 1);

  /** La tarjeta se ve a ~314 px de ancho: `card` (800) sobra para retina. */
  readonly fuentesFotoActual = computed<FuentesFoto | null>(() => {
    const foto = this.galeria()[this.indice()];
    return foto ? fuentesFoto(foto, "card") : null;
  });

  readonly altFoto = computed(
    () => `${this.tipoLabel()} en ${this.propiedad().localidad}`,
  );

  /**
   * Pasa de foto. Único punto de avance del carrusel: lo usan las flechas y el
   * swipe, así que las dos vías ciclan igual en los extremos.
   */
  mover(delta: 1 | -1): void {
    this.indice.update((i) => indiceConWrap(i, delta, this.galeria().length));
  }

  anterior(evento: Event): void {
    evento.stopPropagation();
    this.mover(-1);
  }

  siguiente(evento: Event): void {
    evento.stopPropagation();
    this.mover(1);
  }

  // ── Textos ─────────────────────────────────────────────────────────────────

  readonly tipoLabel = computed(
    () => TIPO_PROPIEDAD_SINGULAR[this.propiedad().tipoPropiedad],
  );

  private readonly precios = computed(() => preciosDeCard(this.propiedad()));

  /**
   * Número pelado, sin sufijo de período: el de venta cuando la propiedad
   * tiene los dos precios, y si no el único que tenga.
   *
   * El `list card` del Figma (`244:1572`) muestra sólo el importe, y el
   * backoffice hace lo mismo (`property-card.component.html`, `price-text`).
   * La documentación funcional sólo fija la moneda ("el precio se mostrará en
   * la moneda en que fue cargada la propiedad"), nada sobre periodicidad.
   * Con las dos pestañas cada importe lleva su rótulo para lectores de
   * pantalla, pero con una sola sigue sin poder distinguirse un alquiler de
   * una venta — ver la pregunta abierta 6 en TAREAS.
   */
  readonly precio = computed(() => this.precios().precio);

  /**
   * Alquiler de las propiedades que además tienen precio de venta (FP-49).
   * Vacío en el resto, y ahí la tarjeta muestra una sola pestaña.
   */
  readonly precioAlquiler = computed(() => this.precios().precioAlquiler);

  /** Logo del broker titular; null con el storage mock (stub://). */
  readonly logoInmobiliaria = computed(() => {
    const contacto = this.propiedad().contacto;
    return contacto && esUrlRenderizable(contacto.logoUrl)
      ? contacto.logoUrl!
      : null;
  });

  /** Fila `description` del Figma: superficie · ambientes · dormitorios. */
  readonly detalles = computed(() => {
    const p = this.propiedad();
    const partes: string[] = [];
    if (p.superficie != null) partes.push(`${p.superficie} m²`);
    if (p.ambientes != null) partes.push(`${p.ambientes} amb.`);
    if (p.dormitorios != null) partes.push(`${p.dormitorios} dorm.`);
    return partes.join(" · ");
  });

  readonly linkWhatsApp = computed(() => {
    const p = this.propiedad();
    // El link a la ficha va en el mensaje: sin él, quien lo recibe no tiene
    // cómo abrir la propiedad.
    const alquiler = this.precioAlquiler();
    const precios = alquiler
      ? `Venta ${this.precio()} · Alquiler ${alquiler}`
      : this.precio();
    const texto =
      `Hola! Me interesa esta propiedad publicada en Phindi: ` +
      `${p.direccion}, ${p.localidad} (${precios}) — ` +
      `${location.origin}/propiedades/${p.id}`;
    return armarLinkWhatsApp(p.contacto?.whatsapp, texto);
  });

  // ── Navegación ─────────────────────────────────────────────────────────────

  verFicha(): void {
    const p = this.propiedad();
    void this.router.navigate(["/propiedades", p.id], {
      state: {
        tarjeta: p,
        // Las fotos que la tarjeta ya tiene en memoria. Desde la grilla eso es
        // la galería completa (BP-03), así que la ficha puede pintarla entera
        // mientras espera el detalle en vez de mostrar la portada sola.
        fotos: this.galeria(),
        volverA: this.volverA() ?? this.router.url,
      },
    });
  }
}
